import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, AlertCircle, Calendar, User as UserIcon, Filter, FolderKanban, Layers, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dimension configuration
const DIMENSIONS = [
  { id: 'cness_tasks', label: 'CNESS Tasks', icon: '📋', color: '#EF4444' },
  { id: 'self_care', label: 'Self Care', icon: '🧘', color: '#10B981' },
  { id: 'rnd', label: 'R&D', icon: '🔬', color: '#6366F1' },
  { id: 'personal_growth', label: 'Personal Growth', icon: '🌱', color: '#F59E0B' },
  { id: 'creative', label: 'Creative', icon: '🎨', color: '#EC4899' },
  { id: 'inspired_work', label: 'Inspired Work', icon: '💡', color: '#8B5CF6' },
];

const STATUS_COLUMNS = [
  { id: 'pending', label: 'Pending', color: '#F59E0B' },
  { id: 'in_progress', label: 'In Progress', color: '#3B82F6' },
  { id: 'completed', label: 'Completed', color: '#10B981' },
];

const HiveBoard = ({ user }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('dimensions'); // 'dimensions' or 'projects'
  const [expandedSections, setExpandedSections] = useState({});
  const [stats, setStats] = useState({ total: 0, pending: 0, inProgress: 0, completed: 0, overdue: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Calculate stats
    let overdueCount = 0;
    tasks.forEach(task => {
      if (task.status !== 'completed' && task.deadline) {
        const now = new Date();
        const deadline = new Date(task.deadline);
        if (deadline < now) overdueCount++;
      }
    });

    setStats({
      total: tasks.length,
      pending: tasks.filter(t => t.status === 'pending').length,
      inProgress: tasks.filter(t => t.status === 'in_progress').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      overdue: overdueCount
    });

    // Initialize all sections as expanded
    const sections = {};
    if (viewMode === 'dimensions') {
      DIMENSIONS.forEach(d => sections[d.id] = true);
      sections['no_dimension'] = true;
    } else {
      projects.forEach(p => sections[p.id] = true);
      sections['no_project'] = true;
    }
    setExpandedSections(sections);
  }, [tasks, viewMode, projects]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [tasksRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/tasks`, { headers, params: { page: 1, page_size: 1000 } }),
        axios.get(`${API_URL}/users`, { headers })
      ]);

      setTasks(tasksRes.data.tasks || []);
      setUsers(usersRes.data);

      try {
        const projectsRes = await axios.get(`${API_URL}/projects`, { headers });
        setProjects(projectsRes.data || []);
      } catch (e) {
        console.log('Projects not available');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load Hive Board');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? u.full_name : 'Unassigned';
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : null;
  };

  const getDeadlineStatus = (deadline, status) => {
    if (status === 'completed') return { label: 'Done', color: '#10B981', icon: <CheckCircle size={12} /> };
    if (!deadline) return { label: 'No Deadline', color: '#6B7280', icon: <Clock size={12} /> };

    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: `${Math.abs(diffDays)}d Overdue`, color: '#EF4444', icon: <AlertCircle size={12} /> };
    if (diffDays <= 2) return { label: `${diffDays}d Left`, color: '#F59E0B', icon: <Clock size={12} /> };
    return { label: `${diffDays}d Left`, color: '#10B981', icon: <Calendar size={12} /> };
  };

  const getPriorityColor = (priority) => {
    const colors = { low: '#10B981', medium: '#F59E0B', high: '#EF4444' };
    return colors[priority] || colors.medium;
  };

  // Strip HTML for preview
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Group tasks by dimension or project
  const getGroupedTasks = () => {
    if (viewMode === 'dimensions') {
      const groups = {};
      DIMENSIONS.forEach(d => { groups[d.id] = { ...d, tasks: [] }; });
      groups['no_dimension'] = { id: 'no_dimension', label: 'Unassigned', icon: '📁', color: '#6B7280', tasks: [] };

      tasks.forEach(task => {
        // Handle dimension as array or string
        const dims = Array.isArray(task.dimension) ? task.dimension : (task.dimension ? [task.dimension] : []);
        
        if (dims.length === 0) {
          groups['no_dimension'].tasks.push(task);
        } else {
          dims.forEach(dimId => {
            if (groups[dimId]) {
              groups[dimId].tasks.push(task);
            } else {
              groups['no_dimension'].tasks.push(task);
            }
          });
        }
      });

      return Object.values(groups).filter(g => g.tasks.length > 0);
    } else {
      const groups = {};
      projects.forEach(p => { groups[p.id] = { id: p.id, label: p.name, icon: '📂', color: p.color || '#6366F1', tasks: [] }; });
      groups['no_project'] = { id: 'no_project', label: 'No Project', icon: '📁', color: '#6B7280', tasks: [] };

      tasks.forEach(task => {
        const projId = task.project_id || 'no_project';
        if (groups[projId]) {
          groups[projId].tasks.push(task);
        } else {
          groups['no_project'].tasks.push(task);
        }
      });

      return Object.values(groups).filter(g => g.tasks.length > 0);
    }
  };

  const TaskCard = ({ task }) => {
    const deadlineInfo = getDeadlineStatus(task.deadline, task.status);

    return (
      <div
        onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
        style={{
          padding: '12px',
          background: 'var(--bg-primary)',
          borderRadius: '8px',
          border: '1px solid var(--border-color)',
          cursor: 'pointer',
          transition: 'all 0.2s',
          marginBottom: '8px'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', lineHeight: '1.4' }}>
            {task.title}
          </h4>
          <span style={{
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: '600',
            background: `${getPriorityColor(task.priority)}15`,
            color: getPriorityColor(task.priority),
            textTransform: 'capitalize',
            flexShrink: 0,
            marginLeft: '8px'
          }}>
            {task.priority}
          </span>
        </div>

        {task.description && (
          <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: '1.4' }}>
            {stripHtml(task.description).substring(0, 60)}...
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: deadlineInfo.color }}>
            {deadlineInfo.icon}
            <span>{deadlineInfo.label}</span>
          </div>
          {task.assigned_to && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-tertiary)' }}>
              <UserIcon size={10} />
              <span>{getUserName(task.assigned_to).split(' ')[0]}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const StatusColumn = ({ status, tasks: columnTasks, groupColor }) => (
    <div style={{ flex: 1, minWidth: '180px' }}>
      <div style={{
        padding: '8px 12px',
        marginBottom: '8px',
        borderRadius: '6px',
        background: `${STATUS_COLUMNS.find(s => s.id === status)?.color}15`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontSize: '12px', fontWeight: '600', color: STATUS_COLUMNS.find(s => s.id === status)?.color }}>
          {STATUS_COLUMNS.find(s => s.id === status)?.label}
        </span>
        <span style={{
          padding: '2px 8px',
          borderRadius: '10px',
          fontSize: '11px',
          fontWeight: '600',
          background: STATUS_COLUMNS.find(s => s.id === status)?.color,
          color: 'white'
        }}>
          {columnTasks.length}
        </span>
      </div>
      <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '4px' }}>
        {columnTasks.map(task => <TaskCard key={task.id} task={task} />)}
        {columnTasks.length === 0 && (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '12px' }}>
            No tasks
          </div>
        )}
      </div>
    </div>
  );

  const GroupSection = ({ group }) => {
    const isExpanded = expandedSections[group.id] !== false;
    const pendingTasks = group.tasks.filter(t => t.status === 'pending');
    const inProgressTasks = group.tasks.filter(t => t.status === 'in_progress');
    const completedTasks = group.tasks.filter(t => t.status === 'completed');

    return (
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => toggleSection(group.id)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            background: `${group.color}10`,
            border: `1px solid ${group.color}30`,
            borderRadius: '10px',
            cursor: 'pointer',
            marginBottom: isExpanded ? '12px' : '0'
          }}
        >
          {isExpanded ? <ChevronDown size={18} style={{ color: group.color }} /> : <ChevronRight size={18} style={{ color: group.color }} />}
          <span style={{ fontSize: '18px' }}>{group.icon}</span>
          <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', flex: 1, textAlign: 'left' }}>
            {group.label}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#F59E0B20', color: '#F59E0B' }}>
              {pendingTasks.length} pending
            </span>
            <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#3B82F620', color: '#3B82F6' }}>
              {inProgressTasks.length} in progress
            </span>
            <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#10B98120', color: '#10B981' }}>
              {completedTasks.length} done
            </span>
          </div>
        </button>

        {isExpanded && (
          <div style={{
            display: 'flex',
            gap: '16px',
            padding: '16px',
            background: 'var(--bg-secondary)',
            borderRadius: '10px',
            border: '1px solid var(--border-color)'
          }}>
            <StatusColumn status="pending" tasks={pendingTasks} groupColor={group.color} />
            <StatusColumn status="in_progress" tasks={inProgressTasks} groupColor={group.color} />
            <StatusColumn status="completed" tasks={completedTasks} groupColor={group.color} />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const groupedTasks = getGroupedTasks();

  return (
    <div className="content-area">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
            🐝 Hive Board
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)' }}>
            Visualize your tasks across dimensions and projects
          </p>
        </div>
        
        {/* View Mode Toggle */}
        <div style={{ display: 'flex', gap: '8px', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '10px' }}>
          <button
            onClick={() => setViewMode('dimensions')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: viewMode === 'dimensions' ? 'var(--primary-color)' : 'transparent',
              color: viewMode === 'dimensions' ? 'white' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Layers size={16} /> By Dimensions
          </button>
          <button
            onClick={() => setViewMode('projects')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: viewMode === 'projects' ? 'var(--primary-color)' : 'transparent',
              color: viewMode === 'projects' ? 'white' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FolderKanban size={16} /> By Projects
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{stats.total}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Total Tasks</div>
        </div>
        <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#F59E0B' }}>{stats.pending}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Pending</div>
        </div>
        <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#3B82F6' }}>{stats.inProgress}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>In Progress</div>
        </div>
        <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#10B981' }}>{stats.completed}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Completed</div>
        </div>
        <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#EF4444' }}>{stats.overdue}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Overdue</div>
        </div>
      </div>

      {/* Grouped Sections */}
      {groupedTasks.length === 0 ? (
        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🐝</div>
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No tasks yet</h3>
          <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>
            Create tasks and assign them to dimensions or projects to see them here
          </p>
          <button onClick={() => navigate('/dashboard/tasks/new')} className="gradient-btn">
            Create Task
          </button>
        </div>
      ) : (
        groupedTasks.map(group => <GroupSection key={group.id} group={group} />)
      )}
    </div>
  );
};

export default HiveBoard;
